import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Checkbox } from 'primereact/checkbox';
import { Toolbar } from 'primereact/toolbar';
import { Tag } from 'primereact/tag';
import axios from 'axios';

import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './Sav.css';

const API_BASE_URL = `${process.env.REACT_APP_API_PATH}`;

// ============================================================
// CONSTANTS
// ============================================================

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const MOBILE_BREAKPOINT = 768;
const TOAST_LIFETIME = 3000;
const TOAST_LIFETIME_LONG = 5000;

// ============================================================
// MAIN COMPONENT
// ============================================================

function Sav() {
    // ============================================================
    // STATE
    // ============================================================

    const [state, setState] = useState({
        savingsData: [],
        loading: false,
        posting: false,
        selectedRows: [],
        showConfirmDialog: false,
        selectAll: false,
        editingCell: null,
        isMobile: window.innerWidth < MOBILE_BREAKPOINT
    });

    const [filters, setFilters] = useState({
        branchCode: null,
        empCode: null,
        groupId: null
    });

    const [filterOptions, setFilterOptions] = useState({
        branches: [],
        employees: [],
        groups: []
    });

    const toast = useRef(null);

    // ============================================================
    // DERIVED STATE
    // ============================================================

    const {
        savingsData,
        loading,
        posting,
        selectedRows,
        showConfirmDialog,
        selectAll,
        isMobile
    } = state;

    const hasSelectedRows = selectedRows.length > 0;
    const hasFilters = Object.values(filters).some(f => f !== null && f !== '');
    const activeFilterCount = Object.values(filters).filter(f => f !== null && f !== '').length;

    // ============================================================
    // COMPUTED VALUES
    // ============================================================

    const totals = useMemo(() => {
        const totalDeposit = savingsData.reduce(
            (sum, row) => sum + (parseFloat(row.deposit) || 0), 0
        );
        const totalWithdrawal = savingsData.reduce(
            (sum, row) => sum + (parseFloat(row.withdrawal) || 0), 0
        );

        // Get latest selected rows from savingsData
        const latestSelectedRows = selectedRows
            .map(selectedRow => 
                savingsData.find(row => row.accountnumber === selectedRow.accountnumber)
            )
            .filter(Boolean);

        const selectedDeposit = latestSelectedRows.reduce(
            (sum, row) => sum + (parseFloat(row.deposit) || 0), 0
        );
        const selectedWithdrawal = latestSelectedRows.reduce(
            (sum, row) => sum + (parseFloat(row.withdrawal) || 0), 0
        );

        return {
            totalDeposit,
            totalWithdrawal,
            selectedDeposit,
            selectedWithdrawal,
            selectedCount: latestSelectedRows.length,
            totalCount: savingsData.length
        };
    }, [savingsData, selectedRows]);

    // ============================================================
    // EFFECTS
    // ============================================================

    useEffect(() => {
        const handleResize = () => {
            setState(prev => ({
                ...prev,
                isMobile: window.innerWidth < MOBILE_BREAKPOINT
            }));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchFilterOptions();
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [filters]);

    // ============================================================
    // API CALLS
    // ============================================================

    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}savings/filter-options`);
            const data = response.data;

            setFilterOptions({
                branches: data.branches?.map(b => ({
                    label: b.name || b.code,
                    value: b.code
                })) || [],
                employees: data.employees?.map(e => ({
                    label: e.name || e.code,
                    value: e.code
                })) || [],
                groups: data.groups?.map(g => ({
                    label: g.name || g.code,
                    value: g.code
                })) || []
            });
        } catch (error) {
            console.error('Failed to load filter options:', error);
            showToast('error', 'Error', 'Failed to load filter options');
        }
    }, []);

    const loadData = useCallback(async () => {
        setState(prev => ({ ...prev, loading: true }));

        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });

            const response = await axios.get(`${API_BASE_URL}savings?${params.toString()}`);
            
            const data = response.data.map(item => ({
                ...item,
                withdrawal: parseFloat(item.withdrawal || item.withdrawl) || 0,
                deposit: parseFloat(item.deposit) || 0
            }));

            setState(prev => ({
                ...prev,
                savingsData: data,
                selectedRows: [],
                selectAll: false,
                loading: false
            }));
        } catch (error) {
            console.error('Failed to load savings data:', error);
            showToast('error', 'Error', 'Failed to load savings data');
            setState(prev => ({ ...prev, loading: false }));
        }
    }, [filters]);

    // ============================================================
    // TOAST HELPERS
    // ============================================================

    const showToast = useCallback((severity, summary, detail, life = TOAST_LIFETIME) => {
        toast.current?.show({ severity, summary, detail, life });
    }, []);

    // ============================================================
    // FILTER HANDLERS
    // ============================================================

    const handleFilterChange = useCallback((field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters({
            branchCode: null,
            empCode: null,
            groupId: null
        });
    }, []);

    // ============================================================
    // DATA UPDATE HANDLERS
    // ============================================================

    const updateRowValue = useCallback((accountnumber, field, value) => {
        setState(prev => ({
            ...prev,
            savingsData: prev.savingsData.map(row =>
                row.accountnumber === accountnumber ? { ...row, [field]: value } : row
            ),
            selectedRows: prev.selectedRows.map(row =>
                row.accountnumber === accountnumber ? { ...row, [field]: value } : row
            )
        }));
    }, []);

    // ============================================================
    // CELL EDIT HANDLERS
    // ============================================================

    const onCellEditComplete = useCallback((event) => {
        const { rowData, newValue, field } = event;
        
        if (field !== 'deposit' && field !== 'withdrawal') return;

        let normalizedValue = 0;
        if (newValue !== undefined && newValue !== null && newValue !== '') {
            normalizedValue = parseFloat(newValue);
            if (isNaN(normalizedValue)) normalizedValue = 0;
        }

        updateRowValue(rowData.accountnumber, field, normalizedValue);
        setState(prev => ({ ...prev, editingCell: null }));
    }, [updateRowValue]);

    const onCellEditCancel = useCallback(() => {
        setState(prev => ({ ...prev, editingCell: null }));
    }, []);

    // ============================================================
    // SELECTION HANDLERS
    // ============================================================

    const onSelectionChange = useCallback((e) => {
        const selected = e.value || [];
        const latestSelected = selected
            .map(selectedRow => 
                savingsData.find(row => row.accountnumber === selectedRow.accountnumber)
            )
            .filter(Boolean);

        setState(prev => ({
            ...prev,
            selectedRows: latestSelected,
            selectAll: latestSelected.length === savingsData.length && savingsData.length > 0
        }));
    }, [savingsData]);

    const onMobileRowSelect = useCallback((rowData) => {
        setState(prev => {
            const exists = prev.selectedRows.some(
                row => row.accountnumber === rowData.accountnumber
            );

            if (exists) {
                return {
                    ...prev,
                    selectedRows: prev.selectedRows.filter(
                        row => row.accountnumber !== rowData.accountnumber
                    )
                };
            }

            const latestRow = savingsData.find(
                row => row.accountnumber === rowData.accountnumber
            ) || rowData;

            return {
                ...prev,
                selectedRows: [...prev.selectedRows, latestRow]
            };
        });
    }, [savingsData]);

    const onMobileSelectAll = useCallback(() => {
        setState(prev => ({
            ...prev,
            selectedRows: prev.selectAll ? [] : [...savingsData],
            selectAll: !prev.selectAll
        }));
    }, [savingsData]);

    const isRowSelected = useCallback((rowData) => {
        return selectedRows.some(row => row.accountnumber === rowData.accountnumber);
    }, [selectedRows]);

    // ============================================================
    // POST TRANSACTIONS
    // ============================================================

    const handlePostTransactions = useCallback(() => {
        if (!hasSelectedRows) {
            showToast('warn', 'No Selection', 'Please select at least one transaction to post');
            return;
        }

        const latestSelectedRows = selectedRows
            .map(selectedRow => 
                savingsData.find(row => row.accountnumber === selectedRow.accountnumber)
            )
            .filter(Boolean);

        const invalidRows = latestSelectedRows.filter(
            row => (parseFloat(row.deposit) || 0) === 0 && (parseFloat(row.withdrawal) || 0) === 0
        );

        if (invalidRows.length === latestSelectedRows.length) {
            showToast('warn', 'Invalid Data', 'Selected rows must have either deposit or withdrawal amount');
            return;
        }

        setState(prev => ({ ...prev, showConfirmDialog: true }));
    }, [selectedRows, savingsData, hasSelectedRows, showToast]);

    const confirmPost = useCallback(async () => {
        setState(prev => ({ ...prev, posting: true, showConfirmDialog: false }));

        try {
            const latestSelectedRows = selectedRows
                .map(selectedRow => 
                    savingsData.find(row => row.accountnumber === selectedRow.accountnumber)
                )
                .filter(Boolean);

            const transactionsToPost = latestSelectedRows.map(row => ({
                accountnumber: row.accountnumber,
                membername: row.membername,
                branchcode: row.branchcode,
                empcode: row.empcode,
                groupid: row.groupid,
                balance: row.balance,
                deposit: parseFloat(row.deposit) || 0,
                withdrawl: parseFloat(row.withdrawal) || 0
            }));

            const requestData = {
                transactions: transactionsToPost,
                createdBy: 1,
                branchCode: filters.branchCode || 'ALL',
                empCode: filters.empCode || 'ALL'
            };

            await axios.post(`${API_BASE_URL}savings/post`, requestData);
            
            showToast('success', 'Success', 'Transactions posted successfully', TOAST_LIFETIME_LONG);
            await loadData();
        } catch (error) {
            console.error('Failed to post transactions:', error);
            showToast(
                'error', 
                'Error', 
                error.response?.data?.message || 'Failed to post transactions',
                TOAST_LIFETIME_LONG
            );
        } finally {
            setState(prev => ({ ...prev, posting: false }));
        }
    }, [selectedRows, savingsData, filters, loadData, showToast]);

    // ============================================================
    // RENDERERS
    // ============================================================

    const renderAccountNumber = useCallback((rowData) => (
        <span className="font-bold text-primary">{rowData.accountnumber}</span>
    ), []);

    const renderMemberName = useCallback((rowData) => (
        <span className="member-name">{rowData.membername}</span>
    ), []);

    const renderDeposit = useCallback((rowData) => {
        const amount = parseFloat(rowData.deposit) || 0;
        return (
            <span className={`deposit-amount ${amount > 0 ? 'font-bold' : ''}`}>
                {amount > 0 ? amount.toFixed(2) : '0.00'}
            </span>
        );
    }, []);

    const renderWithdrawal = useCallback((rowData) => {
        const amount = parseFloat(rowData.withdrawal) || 0;
        return (
            <span className={`withdrawal-amount ${amount > 0 ? 'font-bold' : ''}`}>
                {amount > 0 ? amount.toFixed(2) : '0.00'}
            </span>
        );
    }, []);

    const depositEditor = useCallback((options) => (
        <InputText
            type="number"
            value={options.value || 0}
            onChange={(e) => {
                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                options.editorCallback(isNaN(value) ? 0 : value);
            }}
            className="editable-input deposit"
            style={{ width: '100%' }}
            autoFocus
            step="0.01"
            min="0"
        />
    ), []);

    const withdrawalEditor = useCallback((options) => (
        <InputText
            type="number"
            value={options.value || 0}
            onChange={(e) => {
                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                options.editorCallback(isNaN(value) ? 0 : value);
            }}
            className="editable-input withdrawal"
            style={{ width: '100%' }}
            autoFocus
            step="0.01"
            min="0"
        />
    ), []);

    const renderFilter = useCallback((field, placeholder) => {
        const optionsMap = {
            branchCode: filterOptions.branches,
            empCode: filterOptions.employees,
            groupId: filterOptions.groups
        };

        return (
            <Dropdown
                value={filters[field]}
                options={optionsMap[field] || []}
                onChange={(e) => handleFilterChange(field, e.value)}
                placeholder={placeholder}
                showClear
                style={{ width: isMobile ? '100%' : '250px' }}
                className="p-inputtext-sm"
                filter
                filterPlaceholder="Search..."
                optionLabel="label"
                optionValue="value"
                emptyMessage="No options found"
            />
        );
    }, [filters, filterOptions, isMobile, handleFilterChange]);

    const renderMobileRow = useCallback((rowData) => {
        const selected = isRowSelected(rowData);
        const deposit = parseFloat(rowData.deposit) || 0;
        const withdrawal = parseFloat(rowData.withdrawal) || 0;

        return (
            <Card
                key={rowData.accountnumber}
                className={`mobile-card mb-2 ${selected ? 'mobile-card-selected' : ''}`}
                onClick={() => onMobileRowSelect(rowData)}
            >
                <div className="p-2">
                    {/* Header */}
                    <div className="flex align-items-center gap-2 mb-2">
                        <Checkbox
                            inputId={`cb-${rowData.accountnumber}`}
                            checked={selected}
                            onChange={() => onMobileRowSelect(rowData)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className="font-bold text-primary flex-1">
                            {rowData.accountnumber}
                        </span>
                        <span className="text-sm text-gray-600">{rowData.membername}</span>
                    </div>

                    {/* Details Grid */}
                    <div className="grid">
                        <div className="col-6">
                            <div className="text-sm text-gray-600">Branch</div>
                            <div className="font-medium">{rowData.branchcode}</div>
                        </div>
                        <div className="col-6">
                            <div className="text-sm text-gray-600">Employee</div>
                            <div className="font-medium">{rowData.empcode}</div>
                        </div>
                        <div className="col-12">
                            <div className="text-sm text-gray-600">Group</div>
                            <div className="font-medium">{rowData.groupid}</div>
                        </div>
                        <div className="col-6">
                            <div className="text-sm text-gray-600">Deposit</div>
                            <div className="font-bold text-green-600">
                                <InputText
                                    type="number"
                                    value={deposit === 0 ? '' : deposit}
                                    onChange={(e) => {
                                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                        updateRowValue(rowData.accountnumber, 'deposit', isNaN(value) ? 0 : value);
                                    }}
                                    className="w-full mobile-input deposit"
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-sm text-gray-600">Withdrawal</div>
                            <div className="font-bold text-red-600">
                                <InputText
                                    type="number"
                                    value={withdrawal === 0 ? '' : withdrawal}
                                    onChange={(e) => {
                                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                        updateRowValue(rowData.accountnumber, 'withdrawal', isNaN(value) ? 0 : value);
                                    }}
                                    className="w-full mobile-input withdrawal"
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        );
    }, [isRowSelected, onMobileRowSelect, updateRowValue]);

    // ============================================================
    // TOOLBAR CONTENT
    // ============================================================

    const renderToolbarLeft = useCallback(() => (
        <div className="flex flex-wrap gap-2">
            <Button
                label={isMobile ? '' : 'Refresh'}
                icon="pi pi-refresh"
                className="p-button-outlined"
                onClick={loadData}
                loading={loading}
                tooltip={isMobile ? 'Refresh' : ''}
            />
            <Button
                label={isMobile ? '' : 'Post Selected'}
                icon="pi pi-save"
                className="p-button-success"
                onClick={handlePostTransactions}
                loading={posting}
                disabled={!hasSelectedRows}
                tooltip={isMobile ? 'Post Selected' : ''}
            />
        </div>
    ), [isMobile, loadData, loading, handlePostTransactions, posting, hasSelectedRows]);

    const renderToolbarRight = useCallback(() => {
        if (!hasSelectedRows) return null;
        
        return (
            <Tag 
                severity="info" 
                value={`${selectedRows.length} selected`}
                className="p-mr-2"
            />
        );
    }, [hasSelectedRows, selectedRows.length]);

    // ============================================================
    // SUMMARY
    // ============================================================

    const renderSummary = useCallback(() => {
        if (savingsData.length === 0) return null;

        return (
            <div className="summary-bar bg-blue-50 p-3 border-round mt-3">
                <div className="flex flex-wrap gap-3">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-chart-bar text-primary"></i>
                        <span className="font-semibold">Total:</span>
                        <Badge value={totals.totalCount} severity="info" />
                    </div>
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-arrow-up text-green-500"></i>
                        <span className="font-semibold">Deposit:</span>
                        <span className="text-green-600 font-bold">₹{totals.totalDeposit.toFixed(2)}</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-arrow-down text-red-500"></i>
                        <span className="font-semibold">Withdrawal:</span>
                        <span className="text-red-600 font-bold">₹{totals.totalWithdrawal.toFixed(2)}</span>
                    </div>
                    {totals.selectedCount > 0 && (
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-check-circle text-blue-500"></i>
                            <span className="font-semibold">Selected:</span>
                            <Badge value={`${totals.selectedCount} selected`} severity="success" />
                            <span className="text-green-600">₹{totals.selectedDeposit.toFixed(2)}</span>
                            <span className="text-red-600">₹{totals.selectedWithdrawal.toFixed(2)}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }, [savingsData, totals]);

    // ============================================================
    // CONFIRM DIALOG CONTENT
    // ============================================================

    const renderConfirmDialogContent = useCallback(() => {
        const latestSelectedRows = selectedRows
            .map(selectedRow => 
                savingsData.find(row => row.accountnumber === selectedRow.accountnumber)
            )
            .filter(Boolean);

        const totalDeposit = latestSelectedRows.reduce(
            (sum, row) => sum + (parseFloat(row.deposit) || 0), 0
        );
        const totalWithdrawal = latestSelectedRows.reduce(
            (sum, row) => sum + (parseFloat(row.withdrawal) || 0), 0
        );

        return (
            <div className="confirmation-content">
                <div className="flex flex-column md:flex-row align-items-start gap-3">
                    <i className="pi pi-info-circle" style={{ fontSize: '2.5rem', color: '#3b82f6' }}></i>
                    <div>
                        <h3 className="mt-0 mb-2">Review Transactions</h3>
                        <p className="mb-2">
                            You are about to post <strong>{selectedRows.length}</strong> transaction(s):
                        </p>
                        <div className="bg-gray-50 p-3 border-round mb-2">
                            <div className="flex justify-content-between">
                                <span>Total Deposit:</span>
                                <span className="text-green-600 font-bold">₹{totalDeposit.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-content-between">
                                <span>Total Withdrawal:</span>
                                <span className="text-red-600 font-bold">₹{totalWithdrawal.toFixed(2)}</span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            <i className="pi pi-exclamation-triangle text-yellow-500 mr-1"></i>
                            This action will create transaction records in the database and cannot be undone.
                        </p>
                    </div>
                </div>
            </div>
        );
    }, [selectedRows, savingsData]);

    // ============================================================
    // MAIN RENDER
    // ============================================================

    return (
        <div className="App">
            <Toast ref={toast} />

            <div className="container p-2 p-md-4">
                {/* Header */}
                <div className="header flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold text-primary flex align-items-center gap-2">
                            <i className="pi pi-wallet"></i>
                            Savings Grid
                        </h1>
                        <p className="text-gray-600 text-xs md:text-sm mt-1 hidden sm:block">
                            Manage savings transactions with deposit and withdrawal entries
                        </p>
                    </div>
                    <Toolbar
                        className="p-0 border-none w-full md:w-auto bg-transparent"
                        left={renderToolbarLeft}
                        right={renderToolbarRight}
                    />
                </div>

                {/* Filters */}
                <div className="filters bg-gray-50 p-2 p-md-3 border-round mb-3">
                    <Accordion className="filter-accordion">
                        <AccordionTab
                            header={
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-filter"></i>
                                    <span>Filters</span>
                                    {hasFilters && (
                                        <Badge value={activeFilterCount} severity="info" />
                                    )}
                                </div>
                            }
                        >
                            <div className="flex flex-column gap-3">
                                <div className="flex flex-column gap-1">
                                    <label className="font-semibold text-sm">Branch:</label>
                                    {renderFilter('branchCode', 'Select Branch')}
                                </div>
                                <div className="flex flex-column gap-1">
                                    <label className="font-semibold text-sm">Employee:</label>
                                    {renderFilter('empCode', 'Select Employee')}
                                </div>
                                <div className="flex flex-column gap-1">
                                    <label className="font-semibold text-sm">Group:</label>
                                    {renderFilter('groupId', 'Select Group')}
                                </div>
                                <Button
                                    label="Clear All"
                                    icon="pi pi-times"
                                    className="p-button-outlined p-button-secondary p-button-sm mt-2"
                                    onClick={clearFilters}
                                />
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>

                {/* Data Table / Mobile Grid */}
                <div className="table-wrapper">
                    {isMobile ? (
                        <div className="mobile-grid">
                            {savingsData.length > 0 && (
                                <div className="flex align-items-center gap-2 p-2 bg-gray-50 border-round mb-2">
                                    <Checkbox
                                        inputId="selectAll"
                                        checked={selectAll}
                                        onChange={onMobileSelectAll}
                                    />
                                    <label htmlFor="selectAll" className="font-semibold text-sm">
                                        Select All ({savingsData.length})
                                    </label>
                                    {hasSelectedRows && (
                                        <Badge 
                                            value={`${selectedRows.length} selected`} 
                                            severity="success" 
                                            className="ml-auto"
                                        />
                                    )}
                                </div>
                            )}

                            {loading ? (
                                <div className="flex justify-content-center p-5">
                                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="8" />
                                </div>
                            ) : savingsData.length === 0 ? (
                                <div className="text-center p-5 text-gray-600">
                                    <i className="pi pi-inbox text-4xl mb-2"></i>
                                    <p>No savings records found. Please adjust filters.</p>
                                </div>
                            ) : (
                                savingsData.map(row => renderMobileRow(row))
                            )}
                        </div>
                    ) : (
                        <DataTable
                            value={savingsData}
                            loading={loading}
                            selection={selectedRows}
                            onSelectionChange={onSelectionChange}
                            responsiveLayout="scroll"
                            paginator
                            rows={10}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            className="p-datatable-sm"
                            emptyMessage="No savings records found. Please adjust filters."
                            dataKey="accountnumber"
                            sortMode="multiple"
                            removableSort
                            editMode="cell"
                        >
                            <Column selectionMode="multiple" style={{ width: '3rem' }} headerStyle={{ width: '3rem' }} />
                            <Column field="accountnumber" header="Account Number" body={renderAccountNumber} style={{ minWidth: '150px' }} sortable />
                            <Column field="membername" header="Member Name" body={renderMemberName} style={{ minWidth: '200px' }} sortable />
                            <Column field="branchcode" header="Branch" style={{ minWidth: '120px' }} sortable />
                            <Column field="empcode" header="Employee" style={{ minWidth: '120px' }} sortable />
                            <Column field="groupid" header="Group" style={{ minWidth: '120px' }} sortable />
                            <Column field="balance" header="Balance" style={{ minWidth: '120px' }} sortable />
                            <Column
                                field="deposit"
                                header="Deposit (₹)"
                                body={renderDeposit}
                                editor={depositEditor}
                                style={{ minWidth: '150px' }}
                                sortable
                                onCellEditComplete={onCellEditComplete}
                                onCellEditCancel={onCellEditCancel}
                            />
                            <Column
                                field="withdrawal"
                                header="Withdrawal (₹)"
                                body={renderWithdrawal}
                                editor={withdrawalEditor}
                                style={{ minWidth: '150px' }}
                                sortable
                                onCellEditComplete={onCellEditComplete}
                                onCellEditCancel={onCellEditCancel}
                            />
                        </DataTable>
                    )}
                </div>

                {/* Summary */}
                {renderSummary()}

                {/* Footer Stats */}
                <div className="mt-3 flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="text-sm text-gray-600">
                        <i className="pi pi-check-square text-primary mr-2"></i>
                        Selected: <span className="font-bold text-primary">{selectedRows.length}</span> row(s)
                    </div>
                    <div className="text-sm text-gray-600">
                        <i className="pi pi-database text-primary mr-2"></i>
                        Total: <span className="font-bold">{savingsData.length}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                        <i className="pi pi-filter text-primary mr-2"></i>
                        Filters: {activeFilterCount}
                    </div>
                </div>

                {/* Confirm Dialog */}
                <Dialog
                    header="Confirm Post Transactions"
                    visible={showConfirmDialog}
                    style={{ width: isMobile ? '95%' : '500px' }}
                    modal
                    footer={
                        <div className="flex flex-wrap justify-content-end gap-2">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={() => setState(prev => ({ ...prev, showConfirmDialog: false }))}
                                className="p-button-text flex-1 md:flex-none"
                                disabled={posting}
                            />
                            <Button
                                label="Confirm Post"
                                icon="pi pi-check"
                                onClick={confirmPost}
                                className="p-button-success flex-1 md:flex-none"
                                loading={posting}
                            />
                        </div>
                    }
                    onHide={() => !posting && setState(prev => ({ ...prev, showConfirmDialog: false }))}
                >
                    {renderConfirmDialogContent()}
                </Dialog>

                {/* Loading Overlay */}
                {loading && (
                    <div className="loading-overlay flex justify-content-center align-items-center">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" />
                    </div>
                )}
            </div>
        </div>
    );
}

export default Sav;